import { Component, Output, EventEmitter, signal, inject, OnInit, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { ActualiteService, User } from '../../core/services/actualite.service';
import { TeamService, JobTitle } from '../../core/services/team.service';
import { SessionService } from '../../core/session.service';
import { AuthService } from '../../auth/auth.service';
import { ToastService } from '../../core/services/toast.service';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';
import { LanguageSelectComponent } from '../language-select/language-select.component';
import { resolveAvatarStyle } from '../../core/utils/avatar-color.util';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-user-list-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ConfirmModalComponent, LanguageSelectComponent, TranslatePipe],
  templateUrl: './user-list-modal.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./user-list-modal.scss']
})
export class UserListModalComponent implements OnInit {
  @Output() close = new EventEmitter<void>();

  private api = inject(ActualiteService);
  private teamSvc = inject(TeamService);
  private session = inject(SessionService);
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private toastService = inject(ToastService);
  private translate = inject(TranslateService);

  private allUsers = signal<User[]>([]);
  loading = signal(false);
  error = signal<string | null>(null);
  saving = signal(false);
  currentPage = signal(0);
  pageSize = 20;
  
  selectedUser = signal<User | null>(null);
  showConfirmModal = signal(false);
  showDeleteModal = signal(false);
  userToDelete = signal<User | null>(null);
  
  userForm = this.fb.group({
    first_name: ['', Validators.required],
    last_name: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    locale: ['fr']
  });
  
  // EQUIPEMENTS retiré (2026-09-08) : droit masqué, bloc "Équipement" de /site déprécié -- voir
  // la note DEPRECATED en tête de site.component.html.
  private static readonly PERMISSION_KEYS = [
    'ACCUEIL', 'PREVISIONS', 'SATISFACTION', 'FIL_ACTUALITE', 'QUALITE', 'ACTUALITE',
    'STATISTIQUES', 'ETABLISSEMENT', 'KIT_COMMUNICATION', 'USER_DROIT', 'SUPERVISION',
    'APPLICATION_MOBILE', 'PREVENTION',
  ];

  get availablePermissions(): { key: string; label: string }[] {
    return UserListModalComponent.PERMISSION_KEYS.map(key => ({
      key, label: this.translate.instant('common.permissions.' + key),
    }));
  }

  private readonly waitoraOnlyPermissions = new Set([
    'SUPERVISION', 'APPLICATION_MOBILE', 'ACTUALITE',
    'ACCUEIL', 'PREVISIONS', 'ETABLISSEMENT', 'KIT_COMMUNICATION'
  ]);

  /** Pages V1 sans entrée de menu dédiée une fois Waitora V2 activé (remplacées par le hub /site) */
  private readonly v1OnlyPermissions = new Set([
    'ACCUEIL', 'PREVISIONS', 'ETABLISSEMENT', 'KIT_COMMUNICATION'
  ]);

  get visiblePermissions() {
    const base = this.authService.hasPermission('ADMIN')
      ? this.availablePermissions
      : this.availablePermissions.filter(p => !this.waitoraOnlyPermissions.has(p.key));
    if (this.hasPermission('SUPERVISION')) {
      return base.filter(p => !this.v1OnlyPermissions.has(p.key));
    }
    return base;
  }

  userPermissions = signal<string[]>([]);

  private get isAdminUser(): boolean {
    return this.authService.hasPermission('ADMIN');
  }

  filteredUsers = computed(() => {
    const all = this.allUsers();
    if (this.isAdminUser) return all;
    return all.filter(u => !u.email?.endsWith('@waitora.com'));
  });

  totalUsers = computed(() => this.filteredUsers().length);
  totalPages = computed(() => Math.max(1, Math.ceil(this.totalUsers() / this.pageSize)));

  paginatedUsers = computed(() => {
    const start = this.currentPage() * this.pageSize;
    return this.filteredUsers().slice(start, start + this.pageSize);
  });

  private jobTitles = signal<JobTitle[]>([]);
  private jobTitleColorMap = computed(() => new Map(this.jobTitles().map(j => [j.id, j.color])));
  private jobTitleLabelMap = computed(() => new Map(this.jobTitles().map(j => [j.id, j.label])));

  avatarStyle(u: User): Record<string, string> {
    return resolveAvatarStyle(u, id => this.jobTitleColorMap().get(id));
  }

  jobTitleLabel(user: User): string {
    return (user.job_title_ids ?? []).map(id => this.jobTitleLabelMap().get(id)).filter(Boolean).join(' · ');
  }

  initials(user: User): string {
    const a = (user.first_name?.[0] ?? '').toUpperCase();
    const b = (user.last_name?.[0] ?? '').toUpperCase();
    return `${a}${b}` || '?';
  }

  ngOnInit() {
    this.loadUsers();
    this.teamSvc.getJobTitles().subscribe(list => this.jobTitles.set(list));
  }

  private loadUsers() {
    this.loading.set(true);

    this.api.getUsersPaginated(0, 1000).subscribe({
      next: (response) => {
        this.allUsers.set(response.users);
        this.clampPage();
        this.loading.set(false);
      },
      error: (err) => {
        console.error(err);
        const title = err?.error?.title || 'Erreur';
        const message = err?.error?.message || 'Erreur lors du chargement des utilisateurs';
        this.toastService.show(title, 'error', 5000, message);
        this.loading.set(false);
      }
    });
  }

  private clampPage() {
    if (this.currentPage() >= this.totalPages()) {
      this.currentPage.set(Math.max(0, this.totalPages() - 1));
    }
  }

  previousPage() {
    if (this.currentPage() > 0) {
      this.currentPage.set(this.currentPage() - 1);
    }
  }

  nextPage() {
    if (this.currentPage() < this.totalPages() - 1) {
      this.currentPage.set(this.currentPage() + 1);
    }
  }

  editUser(user: User) {
    this.selectedUser.set(user);
    this.userForm.patchValue({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      locale: user.locale || 'fr'
    });
    this.userPermissions.set(user.permissions || []);
  }

  backToList() {
    this.selectedUser.set(null);
    this.userForm.reset();
    this.userPermissions.set([]);
  }

  saveUser() {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }
    
    this.showConfirmModal.set(true);
  }
  
  confirmSave() {
    this.showConfirmModal.set(false);
    
    const user = this.selectedUser();
    if (!user) return;
    
    this.saving.set(true);
    
    const payload = {
      first_name: this.userForm.value.first_name!,
      last_name: this.userForm.value.last_name!,
      permissions: this.userPermissions(),
      locale: this.userForm.value.locale!
    };
    
    this.api.updateUser(user.id, payload).subscribe({
      next: (response) => {
        this.saving.set(false);
        this.toastService.show(response.title, 'success', 5000, response.message);
        this.authService.refreshPermissions().subscribe(() => {
          this.loadUsers();
          this.backToList();
        });
      },
      error: (err) => {
        this.saving.set(false);
        const title = err?.error?.title || 'Erreur';
        const message = err?.error?.message || 'Erreur lors de la sauvegarde';
        this.toastService.show(title, 'error', 5000, message);
        console.error(err);
      }
    });
  }
  
  cancelSave() {
    this.showConfirmModal.set(false);
  }
  
  togglePermission(permission: string) {
    const current = this.userPermissions();
    if (current.includes(permission)) {
      this.userPermissions.set(current.filter(p => p !== permission));
    } else {
      this.userPermissions.set([...current, permission]);
    }
  }
  
  hasPermission(permission: string): boolean {
    return this.userPermissions().includes(permission);
  }
  
  deleteUser(user: User) {
    this.userToDelete.set(user);
    this.showDeleteModal.set(true);
  }
  
  confirmDelete() {
    const user = this.userToDelete();
    if (!user) return;
    
    this.showDeleteModal.set(false);
    this.saving.set(true);
    
    this.api.deleteUser(user.id).subscribe({
      next: (response) => {
        this.saving.set(false);
        this.userToDelete.set(null);
        this.toastService.show(response.title, 'success', 5000, response.message);
        this.loadUsers();
      },
      error: (err) => {
        this.saving.set(false);
        this.userToDelete.set(null);
        const title = err?.error?.title || 'Erreur';
        const message = err?.error?.message || 'Erreur lors de la suppression';
        this.toastService.show(title, 'error', 5000, message);
        console.error(err);
      }
    });
  }
  
  cancelDelete() {
    this.showDeleteModal.set(false);
    this.userToDelete.set(null);
  }
}
